
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/toaster"
import { ThemeProvider } from "@/components/theme/ThemeProvider"
import { AdminRoleProvider } from "@/contexts/AdminRoleContext"
import { useAuth } from "@/hooks/useAuth";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import BottomNav from "@/components/navigation/BottomNav";
import Discovery from "@/pages/Discovery";
import DiscoveryNew from "@/pages/DiscoveryNew";
import DiscoveryMostBuzzz from "@/pages/DiscoveryMostBuzzz";
import DiscoveryTravelers from "@/pages/DiscoveryTravelers";
import DiscoveryTheirFavorite from "@/pages/DiscoveryTheirFavorite";
import DiscoveryVisitors from "@/pages/DiscoveryVisitors";
import Messages from "@/pages/Messages";
import Matches from "@/pages/Matches";
import Auth from "@/pages/Auth";
import AgeVerification from "@/pages/AgeVerification";
import UserProfileView from "@/components/profile/UserProfileView";
import Gprofile from "@/pages/Gprofile";
import ContentModerationAdmin from "@/pages/ContentModerationAdmin";
import ChatConversation from "@/components/chat/ChatConversation";
import ChatDebug from "@/pages/ChatDebug";
import BlockList from "@/pages/BlockList";
import SignOut from "@/pages/SignOut";
import Settings from "@/pages/Settings";
import Stories from "@/pages/Stories";
import Events from "@/pages/Events";
import CompleteProfile from "@/pages/CompleteProfile";
import Buzzz from "@/pages/Buzzz";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Pricing from "@/pages/Pricing";
import CancellationRights from "@/pages/CancellationRights";
import Favorites from "@/pages/Favorites";
import Kinkipedia from "@/pages/Kinkipedia";
import KinkipediaTopic from "@/pages/KinkipediaTopic";
import KinkipediaArticle from "@/pages/KinkipediaArticle";
import AdminMonitoring from "@/pages/AdminMonitoring";
import UserManual from "@/pages/UserManual";
import Marketing from "@/pages/Marketing";

const queryClient = new QueryClient();


// Component to conditionally render BottomNav based on route
function ConditionalBottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  
  // Don't show bottom nav on these routes
  const hideBottomNavRoutes = ['/auth', '/age-verification', '/admin/content-moderation'];
  
  console.log('🔍 ConditionalBottomNav render:', { 
    pathname: location.pathname, 
    hasUser: !!user,
    shouldShow: !!user && !hideBottomNavRoutes.includes(location.pathname)
  });
  
  // Don't show if user is not logged in or on excluded routes
  if (!user || hideBottomNavRoutes.includes(location.pathname)) {
    console.log('❌ BottomNav hidden - no user or on excluded route');
    return null;
  }
  
  console.log('✅ BottomNav should be visible');
  return <BottomNav />;
}

function AppContent() {
  const { user, loading } = useAuth();

  console.log('🔐 App auth state:', { 
    hasUser: !!user, 
    userId: user?.id, 
    loading,
    currentPath: window.location.pathname 
  });

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <AlphaLoader size="large" />
      </div>
    );
  }

  const isLoggedIn = !!user;
  console.log('🔐 User logged in status:', isLoggedIn);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-carbon relative">
        <Routes>
          <Route path="/age-verification" element={<AgeVerification />} />
          <Route path="/auth" element={!isLoggedIn ? <Auth /> : <Navigate to="/discovery" />} />
          <Route path="/complete-profile" element={isLoggedIn ? <CompleteProfile /> : <Navigate to="/auth" />} />
          <Route path="/discovery" element={isLoggedIn ? <Discovery /> : <Navigate to="/auth" />} />
          <Route path="/discovery/new" element={isLoggedIn ? <DiscoveryNew /> : <Navigate to="/auth" />} />
          <Route path="/discovery/most-buzzz" element={isLoggedIn ? <DiscoveryMostBuzzz /> : <Navigate to="/auth" />} />
          <Route path="/discovery/travelers" element={isLoggedIn ? <DiscoveryTravelers /> : <Navigate to="/auth" />} />
          <Route path="/discovery/their-favorite" element={isLoggedIn ? <DiscoveryTheirFavorite /> : <Navigate to="/auth" />} />
          <Route path="/discovery/visitors" element={isLoggedIn ? <DiscoveryVisitors /> : <Navigate to="/auth" />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/stories" element={isLoggedIn ? <Stories /> : <Navigate to="/auth" />} />
          <Route path="/messages" element={isLoggedIn ? <Messages /> : <Navigate to="/auth" />} />
          <Route path="/messages/:matchId" element={isLoggedIn ? <ChatConversation /> : <Navigate to="/auth" />} />
          <Route path="/matches" element={isLoggedIn ? <Matches /> : <Navigate to="/auth" />} />
          <Route path="/events" element={isLoggedIn ? <Events /> : <Navigate to="/auth" />} />
          <Route path="/buzzz" element={isLoggedIn ? <Buzzz /> : <Navigate to="/auth" />} />
          <Route path="/favorites" element={isLoggedIn ? <Favorites /> : <Navigate to="/auth" />} />
          <Route path="/kinkipedia" element={isLoggedIn ? <Kinkipedia /> : <Navigate to="/auth" />} />
          <Route path="/kinkipedia/topic/:topicId" element={isLoggedIn ? <KinkipediaTopic /> : <Navigate to="/auth" />} />
          <Route path="/kinkipedia/article/:articleId" element={isLoggedIn ? <KinkipediaArticle /> : <Navigate to="/auth" />} />
          <Route path="/gprofile/:userId" element={isLoggedIn ? <Gprofile /> : <Navigate to="/auth" />} />
          <Route path="/block-list" element={isLoggedIn ? <BlockList /> : <Navigate to="/auth" />} />
          <Route path="/settings" element={isLoggedIn ? <Settings /> : <Navigate to="/auth" />} />
          <Route path="/sign-out" element={isLoggedIn ? <SignOut /> : <Navigate to="/auth" />} />
          <Route path="/admin/content-moderation" element={isLoggedIn ? <ContentModerationAdmin /> : <Navigate to="/auth" />} />
          <Route path="/admin/monitoring" element={isLoggedIn ? <AdminMonitoring /> : <Navigate to="/auth" />} />
          <Route path="/user-manual" element={isLoggedIn ? <UserManual /> : <Navigate to="/auth" />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/cancellation-rights" element={<CancellationRights />} />
          <Route path="/" element={<Navigate to="/age-verification" />} />
        </Routes>
        
        {/* Render BottomNav as a portal-like component at the root level */}
        <div className="fixed bottom-0 left-0 right-0 z-[99999] pointer-events-none">
          <div className="pointer-events-auto">
            <ConditionalBottomNav />
          </div>
        </div>
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={true}
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <AdminRoleProvider>
          <Toaster />
          <AppContent />
        </AdminRoleProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
